import React, {useMemo, useRef, useEffect, useState} from 'react'
import * as d3 from 'd3'
import Colors from '../../../utils/Colors.js'
import prefersReducedMotion from '../../utils/PrefersReducedMotion.js'
import {Card, P} from '../UI/index.js'

export interface HorizontalBarChartStackedProps {
    width: number
    height?: number
    barHeight: number
    data: {
        values: {
            value: number
            key: string
            title?: string
        }[]
        name: string
    }[]
    colorMap: {
        [key: string]: string
    }
    activeXTick?: string
    onClickXTick?: (tick: string) => void
    highlightedXTick?: string
    showNameInShortBar?: boolean
    showValueInBar?: boolean
    showGrid?: boolean
    animated?: boolean
    isPercentage?: boolean
    combineStackTitles?: boolean
    disableHover?: boolean
}

/**
 * @param {BarChartProps} props
 *
 * @returns {React.JSX.Element} The rendered BarChartStacked component
 */
export default function HorizontalBarChartStacked({width, height, barHeight = 22, data, colorMap, activeXTick, onClickXTick, highlightedXTick, showNameInShortBar = true, showValueInBar = true, showGrid = true, animated = !prefersReducedMotion, isPercentage, combineStackTitles = false, disableHover = false}: HorizontalBarChartStackedProps): React.JSX.Element {
    const highlightedXTickRect = useRef<SVGGElement>()
    const [hoverXTick, setHoverXTick] = useState<string | null>(null)
    const margin = {top: 20, right: 30, bottom: 30, left: width * 0.3}
    const barSpacing = 16
    const ownHeight = Math.ceil((data.length + 0.1) * (barHeight + barSpacing)) + margin.top + margin.bottom
    if (!height) {
        height = ownHeight
    } else if (height < ownHeight) {
        height = ownHeight
    }

    const svgRef = useRef(null)

    const dataIsEmpty = useMemo(() => data.length === 0, [data])

    useEffect(() => {
        if (svgRef.current) {
            const svg = d3.select(svgRef.current)
            const count = svg.selectAll('.bar').size()
            const duration = 750
            const delay = 50
            const totalDur = Math.min((duration + (count - 1) * delay), 1_000) // max duration of 1 second
            const bars = svg.selectAll('.bar')
            const labels = bars.select((_, i, bars) => {
                const el = bars[i] as Element
                return el.nextElementSibling
            })

            if (animated) {
                const delayDurPerBar = totalDur / bars.size()

                labels.attr('x', (_, i, labels) => {
                    const el = labels[i]
                    if (!el) {
                        return null
                    }

                    const currentX = el.getAttribute('x')

                    if (currentX) {
                        return currentX
                    }

                    const value = el.getAttribute('data-value')

                    if (!value) {
                        return null
                    }

                    return xScale(parseFloat(value)) - xScale(minimumX)
                })

                bars.transition()
                    .duration(duration)
                    .delay((_, i) => i * delayDurPerBar)
                    .attr('width', (_, i, bars) => {
                        const el = bars[i] as Element
                        if (!el) {
                            return null
                        }

                        const valueStart = el.getAttribute('data-value-start')
                        const valueEnd = el.getAttribute('data-value-end')

                        if (!valueStart || !valueEnd) {
                            return null
                        }

                        return xScale(parseFloat(valueEnd)) - xScale(minimumX)
                    })
                    .each((_, i, bars) => {
                        const el = bars[i] as Element

                        if (!el) {
                            return null
                        }

                        const value = el.getAttribute('data-value-end')

                        if (!value) {
                            return null
                        }

                        const label = d3.select(el.nextElementSibling)
                        label.transition()
                            .duration(duration)
                            .attr('x', xScale(parseFloat(value)) - xScale(minimumX))

                        label.transition()
                            .duration(200)
                            .ease(d3.easeCubicInOut)
                            .delay(totalDur)
                            .style('opacity', 1)
                    })
            } else {
                bars.attr('width', (_, i, bars) => {
                    const el = bars[i] as Element

                    if (!el) {
                        return null
                    }

                    const value = el.getAttribute('data-value')

                    if (!value) {
                        return null
                    }

                    return xScale(parseFloat(value)) - xScale(minimumX)
                })

                labels.attr('x', (_, i, labels) => {
                    const el = labels[i] as Element

                    if (!el) {
                        return null
                    }

                    const value = el.getAttribute('data-value')

                    if (!value) {
                        return null
                    }

                    return xScale(parseFloat(value)) - xScale(minimumX)
                })
            }
        }
    }, [data, margin.left])

    useEffect(() => {
        if (highlightedXTick) {
            setTimeout(() => {
                highlightedXTickRect.current?.scrollIntoView({block: 'center'})
            }, 1000)
        }
    }, [highlightedXTick, highlightedXTickRect.current])

    const series = data.map((d) => {
        const stacks: {
            key: string
            start: number
            end: number
            title?: string
        }[] = []

        let currentValue = 0
        d.values.forEach((v) => {
            stacks.push({
                key: v.key,
                start: isPercentage ? Math.min(currentValue, 100) : currentValue,
                end: isPercentage ? v.value : currentValue + v.value,
                title: v.title
            })

            currentValue += v.value
        })

        return {
            name: d.name,
            stacks,
            total: isPercentage ? (d.values.find((v) => v.key === 'total')?.value || 100) : currentValue
        }
    })

    const minimumX = 0

    const dataMax = d3.max(series, (serie) => serie.total) || 0

    const roundingFactor = Math.pow(10, Math.floor(Math.log10(dataMax)))

    const maximumX = dataIsEmpty ? 100 : Math.ceil(dataMax / roundingFactor) * roundingFactor

    const xScale = useMemo(() => {
        return d3.scaleLinear()
            .domain([minimumX, maximumX])
            .range([margin.left, (width) - margin.right])
    }, [data, width, margin.left])

    const yScale = useMemo(() => {
        return d3.scaleBand()
            .domain(data.map((d) => d.name))
            .range([margin.top, (ownHeight - margin.bottom)])
            .padding(0.1)
    }, [data, ownHeight])

    const format = useMemo(() => {
        const customLocale = d3.formatLocale({
            decimal: ',',
            thousands: '.',
            grouping: [3], // Group by thousands
            currency: ['', '']
        })

        return customLocale.format(',.0f') // Format to 0 decimal places
    }, [xScale])

    const xTicks = xScale.ticks(maximumX < 5 ? maximumX : 5)

    const xTickElements = xTicks.map((tick) => (
        <g key={tick} transform={`translate(${xScale(tick)}, 0)`}>
            <text y={0} dy='.9em' textAnchor='middle'>{format(tick)}{isPercentage ? '%' : ''}</text>
        </g>
    ))

    // xTickElements.pop()

    const yTicks = yScale.domain().map((tick) => {
        let displayTick = tick

        if (tick.includes('Verwijderde koerier')) {
            displayTick = tick.split('_')[0]
        }

        return (
            <g
                key={tick}
                style={!disableHover ? {cursor: 'pointer'} : undefined}
                data-value={tick}
                transform={`translate(0, ${(yScale(tick) || 0) + (yScale.bandwidth() - barSpacing) / 2})`}
                onClick={(onClickXTick && !disableHover) ? () => onClickXTick(tick) : undefined}
                onMouseEnter={() => setHoverXTick(tick)}
                onMouseLeave={() => setHoverXTick(null)}
            >
                <foreignObject height={20} width={width} x={0} y='-.6em'>
                    <P style={{color: Colors.black, width: xScale(minimumX) - 20, fontWeight: highlightedXTick === tick ? 'bold' : 'normal'}} ellipsis>{displayTick}</P>
                </foreignObject>
            </g>
        )
    })

    return (
        <>
            {dataIsEmpty &&
                <Card style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', padding: 24}}>
                    Geen data gevonden in de geselecteerde periode.
                </Card>
            }
            <svg style={{height: '100%'}} ref={svgRef} width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
                {activeXTick &&
                    <g transform={`translate(0, ${(yScale(activeXTick) || 0) + (yScale.bandwidth() - barSpacing) / 2})`}>
                        <rect
                            fill='rgba(0, 0, 0, 0.1)'
                            width={xScale(maximumX)}
                            height={24}
                            y='-.7em'
                        />
                    </g>
                }
                {(highlightedXTick || (hoverXTick && !disableHover)) &&
                    <g transform={`translate(0, ${(yScale(highlightedXTick || hoverXTick) || 0) + (yScale.bandwidth() - barSpacing) / 2})`} ref={highlightedXTickRect}>
                        <rect
                            fill='rgba(0, 0, 0, 0.1)'
                            width={xScale(maximumX)}
                            height={24}
                            y='-.7em'
                        />
                    </g>
                }
                <g>
                    {xTickElements}
                </g>
                <g>
                    {yTicks}
                </g>
                <g>
                    {showGrid &&
                        <g>
                            {/* x-axis vertical lines */}
                            <g>
                                <line x1={xScale(minimumX)} x2={xScale(minimumX)} y1={height - margin.bottom} y2={margin.top} stroke={Colors.grey30} strokeWidth={1} />
                                {
                                    xTicks.map((tick) => (
                                        <line key={tick} x1={xScale(tick)} x2={xScale(tick)} y1={height - margin.bottom} y2={margin.top} stroke={Colors.grey30} strokeWidth={1} />
                                    ))
                                }
                            </g>
                            {/* y-axis horizontal lines */}
                            <g>
                                <line x1={xScale(minimumX)} x2={xScale(maximumX)} y1={margin.top} y2={margin.top} stroke={Colors.grey30} strokeWidth={1} />
                                {/* {yScale.domain().map((tick) => (
                            <line key={tick} x1={xScale(minimumX)} x2={xScale(maximumX)} y1={yScale(tick) + (yScale.bandwidth() - barSpacing) / 2} y2={yScale(tick) + (yScale.bandwidth() - barSpacing) / 2} stroke={Colors.grey30} strokeWidth={1}/>
                        ))} */}
                                <line x1={xScale(minimumX)} x2={xScale(maximumX)} y1={height - margin.bottom} y2={height - margin.bottom} stroke={Colors.grey30} strokeWidth={1} />
                            </g>
                        </g>
                    }
                    {series.flatMap((serie) => {
                        const shortBar = xScale(serie.total) - xScale(minimumX) < 160
                        let title: string | undefined
                        if (combineStackTitles) {
                            title = serie.stacks.map((stack) => stack.title ? `${stack.title}` : `${stack.key}: ${stack.end - stack.start}`).join(', ')
                        }

                        return (
                            <g key={serie.name}>
                                {serie.stacks.toReversed().map((stack, i) => (
                                    <g
                                        key={i}
                                        style={!disableHover ? {cursor: 'pointer'} : undefined}
                                        transform={`translate(${xScale(minimumX)}, ${yScale(serie.name)})`}
                                        onMouseEnter={() => setHoverXTick(serie.name)}
                                        onMouseLeave={() => setHoverXTick(null)}
                                        onClick={(onClickXTick && !disableHover) ? () => onClickXTick(serie.name) : undefined}
                                    >
                                        <title>{title ? title : (stack.title ? stack.title : `${stack.key} - ${stack.end - stack.start}`)}</title>
                                        <rect
                                            fill={colorMap[stack.key] || '#3077B6'}
                                            width={0}
                                            height={yScale.bandwidth() - barSpacing}
                                            className='bar'
                                            data-value-start={stack.start}
                                            data-value-end={stack.end}
                                        />
                                        <text
                                            y={(yScale.bandwidth() - barSpacing) / 2}
                                            dy='0.35em'
                                            dx={shortBar ? 16 : -12}
                                            fill={shortBar ? Colors.textMedium : Colors.textLight}
                                            textAnchor={shortBar ? 'start' : 'end'}
                                            style={{opacity: (!animated) ? 1 : 0}}
                                            className='bar-label'
                                            data-value={stack.end}
                                        >
                                            {showNameInShortBar ? `${stack.key} - ` : ''}{showValueInBar ? `${format(stack.end)}${isPercentage ? '%' : ''}` : ''}
                                        </text>
                                    </g>
                                ))}
                            </g>
                        )
                    })}
                </g>
            </svg>
        </>
    )
}
